import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { ProfilesForChat } from "@/lib/types/profile.types";
import { getProfileDisplayName } from "@/lib/utils/client-utils";
import { getMessagePreview, formatMessageTime } from "./inbox.utils";

interface Message {
	id: string;
	content: unknown;
	created_at: string;
	created_by: string | null;
}

interface InboxHoverMessagesProps {
	channelId: string;
	lastReadAt: string | null;
	userId?: string;
	profilesForChat: Record<string, ProfilesForChat>;
}

export const InboxHoverMessages: React.FC<InboxHoverMessagesProps> = ({
	channelId,
	lastReadAt,
	userId,
	profilesForChat,
}) => {
	const [messages, setMessages] = useState<Message[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;

		const fetchMessages = async () => {
			let query = supabase
				.from("messages_with_actions")
				.select("id, content, created_at, created_by")
				.eq("channel_id", channelId)
				.order("created_at", { ascending: false })
				.limit(10);

			if (lastReadAt) {
				query = query.gt("created_at", lastReadAt);
				if (userId) {
					query = query.neq("created_by", userId);
				}
			}

			const { data } = await query;
			if (!cancelled && data) {
				setMessages(
					data
						.filter(
							(
								m
							): m is typeof m & {
								id: string;
								created_at: string;
							} => !!m.id && !!m.created_at
						)
						.reverse()
				);
			}
			if (!cancelled) {
				setLoading(false);
			}
		};

		fetchMessages();
		return () => {
			cancelled = true;
		};
	}, [channelId, lastReadAt]);

	if (loading) {
		return (
			<div className="flex items-center justify-center py-4">
				<Loader2 className="h-4 w-4 animate-spin text-gray-400" />
			</div>
		);
	}

	if (messages.length === 0) {
		return (
			<p className="text-xs text-gray-400 py-2 text-center">
				Keine Nachrichten
			</p>
		);
	}

	return (
		<div className="flex flex-col gap-2">
			{messages.map((msg) => {
				const profile = profilesForChat[msg.created_by ?? ""];
				const isOwn = userId && msg.created_by === userId;
				const senderName = isOwn
					? "Sie"
					: profile
						? getProfileDisplayName(profile)
						: "Unbekannt";
				const preview = getMessagePreview(msg.content, { maxLen: 120 });

				return (
					<div key={msg.id} className="flex flex-col gap-0.5">
						<div className="flex items-baseline justify-between gap-2">
							<span className="text-xs font-semibold text-gray-700 truncate">
								{senderName}
							</span>
							<span className="text-[10px] text-gray-400 flex-shrink-0">
								{formatMessageTime(msg.created_at)}
							</span>
						</div>
						{preview && (
							<p className="text-xs text-gray-500 line-clamp-2">
								{preview}
							</p>
						)}
					</div>
				);
			})}
		</div>
	);
};
